import System, {
    PElement,
    T1Element,
    // TtElement,
    IElement,
    DElement,
    // PT1Element,
    // DT1Element,
    Hysteresis,
} from './systems';

// --------------------------------------------------------------------------------

export class ManualController extends System {

    static traces = ['y', 'u', 'u_sat', 'u_min', 'u_max'];

    constructor(parameters) {
        super();
        this.parameters.u_man = parameters.u_man !== undefined ? parameters.u_man : 1;
    }

    getNextValues(x = 0) {
        this.x_last = x;
        this.y_last = this.parameters.u_man;
        return {y: this.y_last};
    }

    static getName(){
        return 'ManualController';
    }

}

// --------------------------------------------------------------------------------

export class TwoPositionController extends Hysteresis {

    static traces = ['y', 'r', 'u', 'u_sat', 'u_min', 'u_max', 'u_lower', 'u_upper', 'e', 'e_lower', 'e_upper'];

    static getName(){
        return 'TwoPositionController';
    }

}

// --------------------------------------------------------------------------------

export class ThreePositionController extends System {

    static traces = ['y', 'r', 'u', 'u_sat', 'u_min', 'u_max', 'u_lower', 'u_middle', 'u_upper', 'e', 'e_lower', 'e_upper', 'e_middle1', 'e_middle2'];

    constructor(parameters) {
        super();
        this.lowerhysteresis = new Hysteresis({
            e_lower: parameters.e_lower !== undefined ? parameters.e_lower : -3,
            e_upper: parameters.e_middle1 !== undefined ? parameters.e_middle1 : -1.5,
            u_lower: parameters.u_lower !== undefined ? parameters.u_lower : -15,
            u_upper: parameters.u_middle !== undefined ? parameters.u_middle : 0
        });
        this.upperhysteresis = new Hysteresis({
            e_lower: parameters.e_middle2 !== undefined ? parameters.e_middle2 : -3,
            e_upper: parameters.e_upper !== undefined ? parameters.e_upper : -1.5,
            u_lower: 0,
            u_upper:
                (parameters.u_upper !== undefined ? parameters.u_upper : 15) -
                (parameters.u_middle !== undefined ? parameters.u_middle : 0),
        });
    }

    updateParameters(parameters) {
        this.lowerhysteresis.updateParameters({
            e_lower: parameters.e_lower,
            e_upper: parameters.e_middle1,
            u_lower: parameters.u_lower,
            u_upper: parameters.u_middle
        });
        this.upperhysteresis.updateParameters({
            e_lower: parameters.e_middle2,
            e_upper: parameters.e_upper,
            u_upper:
                (parameters.u_upper !== undefined ? parameters.u_upper : this.upper.u_upper) -
                (parameters.u_middle !== undefined ? parameters.u_middle : this.lower.u_upper),
        });
    }

    getNextValues(x) {
        this.y_last = this.lowerhysteresis.getNextValues(x).y + this.upperhysteresis.getNextValues(x).y;
        return {y: this.y_last};
    }

    reset() {
        super.reset();
        this.lowerhysteresis.reset();
        this.upperhysteresis.reset();
    }

    static getName(){
        return 'ThreePositionController';
    }

}

// --------------------------------------------------------------------------------

export class PController extends System {

    static traces = ['y', 'r', 'u', 'u_sat', 'u_min', 'u_max', 'e'];

    constructor(parameters) {
        super();
        this.p = new PElement({k_p: parameters.k_p !== undefined ? parameters.k_p : 10});
    }

    updateParameters(parameters) {
        this.p.updateParameters(parameters);
    }

    getNextValues(x) {
        this.y_last = this.p.getNextValues(x).y;
        return {y: this.y_last};
    }

    reset() {
        super.reset();
    }

    static getName(){
        return 'PController';
    }

}

// --------------------------------------------------------------------------------

export class PIController extends System {

    static traces = ['y', 'r', 'u', 'u_sat', 'u_min', 'u_max', 'e', 'e_int'];

    constructor(parameters) {
        super();
        this.p = new PElement({k_p: parameters.k_p !== undefined ? parameters.k_p : 10});
        this.i = new IElement({t_i: parameters.t_i !== undefined ? parameters.t_i : 1});
    }

    updateParameters(parameters) {
        this.p.updateParameters(parameters);
        this.i.updateParameters(parameters);
    }

    getNextValues(x, additionalParams) {
        if(!additionalParams.freezeIntegrator){
            const nextValues = this.i.getNextValues(x);
            this.e_int_last = nextValues.integral;
            this.y_integrator = nextValues.y;
        }
        this.y_last = this.p.getNextValues(x + this.y_integrator).y;
        return {y: this.y_last, e_int: this.e_int_last};
    }

    reset() {
        super.reset();
        this.i.reset();
    }

    static getName(){
        return 'PIController';
    }

}

// --------------------------------------------------------------------------------

export class PDController extends System {

    static traces = ['y', 'r', 'u', 'u_sat', 'u_min', 'u_max', 'e', 'e_diff'];

    constructor(parameters) {
        super();
        this.p = new PElement({k_p: parameters.k_p !== undefined ? parameters.k_p : 10});
        this.d = new DElement({t_d: parameters.t_d !== undefined ? parameters.t_d : 1});
        this.lowPass = new T1Element({t_1: parameters.t_f !== undefined ? parameters.t_f : 0});
    }

    updateParameters(parameters) {
        this.p.updateParameters(parameters);
        this.d.updateParameters(parameters);
        this.lowPass.updateParameters({t_1: parameters.t_f});
    }

    getNextValues(x, additionalParams) {
        const nextDiffValues = this.d.getNextValues(this.lowPass.getNextValues(additionalParams.yNegative ?? x).y);
        this.y_last = this.p.getNextValues(x + nextDiffValues.y).y;
        return {y: this.y_last, e_diff: nextDiffValues.differential};
    }

    reset() {
        super.reset();
        this.d.reset();
    }

    static getName(){
        return 'PDController';
    }
}


// --------------------------------------------------------------------------------

export class PIDController extends System {

    static traces = ['y', 'r', 'u', 'u_sat', 'u_min', 'u_max', 'e', 'e_int', 'e_diff'];

    constructor(parameters) {
        super();
        this.p = new PElement({k_p: parameters.k_p !== undefined ? parameters.k_p : 10});
        this.i = new IElement({t_i: parameters.t_i !== undefined ? parameters.t_i : 1});
        this.d = new DElement({t_d: parameters.t_d !== undefined ? parameters.t_d : 1});
        this.lowPass = new T1Element({t_1: parameters.t_f !== undefined ? parameters.t_f : 0});
        this.e_int_last = 0;
    }

    updateParameters(parameters) {
        this.p.updateParameters(parameters);
        this.i.updateParameters(parameters);
        this.d.updateParameters(parameters);
        this.lowPass.updateParameters({t_1: parameters.t_f});
    }

    getNextValues(x, additionalParams) {
        if(!additionalParams.freezeIntegrator){
            const nextValues = this.i.getNextValues(x);
            this.e_int_last = nextValues.integral;
            this.y_integrator = nextValues.y;
        }
        const nextDiffValues = this.d.getNextValues(this.lowPass.getNextValues(additionalParams.yNegative ?? x).y);
        this.y_last = this.p.getNextValues(x + this.y_integrator + nextDiffValues.y).y;
        return {y: this.y_last, e_int: this.e_int_last, e_diff: nextDiffValues.differential};
    }

    reset() {
        super.reset();
        this.i.reset();
        this.d.reset();
    }

    static getName(){
        return 'PIDController';
    }

}

// --------------------------------------------------------------------------------
